"use client";

import { useRef, useState } from "react";
import {
  useAudioRecording,
  type AudioRecordingHandlers,
  useDebug,
} from "@huub-web/ui";

import {
  WebSocketChannel,
  type WebSocketVoiceFullTranscriptMessage,
  type WebSocketVoiceTranscriptionMessage,
} from "../../types";
import { useWebSocket } from "./useWebSocket";
import { useWebSocketListener } from "./useWebSocketListener";

export type SpeechToTextHandlers = {
  onTranscript?: (transcript: string) => void;
  onVolume?: AudioRecordingHandlers["onVolume"];
  onStop?: () => void;
};

/**
 * Hook for recording voice input and sending it to the server for transcription.
 */
export const useSpeechToText = (handlers: SpeechToTextHandlers = {}) => {
  const { sendMessage } = useWebSocket();
  const { log } = useDebug();

  const [isProcessingRecording, setIsProcessingRecording] = useState(false);
  const [transcription, setTranscription] = useState("");
  const transcriptionRef = useRef(transcription);

  const audioRecordingState = useAudioRecording({
    onStart: () => {
      sendMessage(WebSocketChannel.VOICE_START, {});
    },
    onDataAvailable: (data) => {
      sendMessage(WebSocketChannel.VOICE_DATA, data);
    },
    onStop: () => {
      handlers.onStop?.();
      setIsProcessingRecording(true);

      setTimeout(() => {
        sendMessage(WebSocketChannel.VOICE_STOP, {});
      }, 1000);
    },
    onVolume: handlers.onVolume,
  });

  // Listen for voice transcription messages
  useWebSocketListener<WebSocketVoiceTranscriptionMessage>(
    WebSocketChannel.VOICE_TRANSCRIPTION,
    (received) => {
      setTranscription(received.data.text);
      transcriptionRef.current = received.data.text;
    },
  );

  // Listen for voice full transcript messages
  useWebSocketListener<WebSocketVoiceFullTranscriptMessage>(
    WebSocketChannel.VOICE_FULL_TRANSCRIPT,
    (received) => {
      log(
        "cyan",
        "useSpeechToText",
        `Full transcript received: "${received.data.message}"`,
      );

      transcriptionRef.current = "";
      setTranscription("");
      handlers.onTranscript?.(received.data.message);
      setIsProcessingRecording(false);
    },
  );

  return {
    transcription,
    isProcessingRecording,
    ...audioRecordingState,
  };
};
